import {FormControl, FormControlLabel, InputLabel, MenuItem, Select, Switch} from '@mui/material'
import {DropdownItem} from '@viptech/react-components'
import {useEffect, useMemo, useState} from 'react'
import {ValidateUserPermissions} from 'src/common/utils/ValidatePermissions'
import Dropdown from 'src/components/dropdown/Dropdown'
import {Input} from 'src/components/input/Input'
import {FormModal} from 'src/components/modal/FormModal'
import {useCompany} from 'src/contexts/CompanyContext'
import useCreateAlarmCenter from 'src/hooks/api/alarm-center/useCreateAlarmCenter'
import useUpdateAlarmCenter from 'src/hooks/api/alarm-center/useUpdateAlarmCenter'
import useClients from 'src/pages/lists/hooks/useClients'
import api from 'src/services/api'
import {AlarmCenter, AlarmCenterLineModel} from 'src/services/api/endpoints/AlarmCenterEndpoint'
import {isValidAccountNumber, isValidHardwareId, LINE_MODEL_OPTIONS} from '../alarmCentersHelpers'

type Props = {
  isOpen: boolean
  alarmCenter?: AlarmCenter | null
  onClose: () => void
}

type FormState = {
  hardwareId: string
  accountNumber: string
  lineModel: AlarmCenterLineModel | ''
  companySelected: DropdownItem
  clientSelected: DropdownItem
  isActive: boolean
}

const emptyDropdown = {} as DropdownItem

function getInitialForm(alarmCenter?: AlarmCenter | null): FormState {
  return {
    hardwareId: alarmCenter?.hardwareId ?? '',
    accountNumber: alarmCenter?.accountNumber ?? '',
    lineModel: alarmCenter?.lineModel ?? '',
    companySelected: alarmCenter ? {id: `${alarmCenter.companyId}`, label: ''} : emptyDropdown,
    clientSelected: alarmCenter ? {id: `${alarmCenter.clientId}`, label: ''} : emptyDropdown,
    isActive: alarmCenter?.isActive ?? true,
  }
}

type FormProps = {
  alarmCenter?: AlarmCenter | null
  onClose: () => void
}

function AlarmCentersForm({alarmCenter, onClose}: FormProps) {
  const isEdit = alarmCenter != null
  const canCreate = ValidateUserPermissions('alarm-centers:create')
  const canUpdate = ValidateUserPermissions('alarm-centers:update')
  const {companies} = useCompany()
  const {clients: clientItems, getClients} = useClients()

  const [form, setForm] = useState<FormState>(() => getInitialForm(alarmCenter))
  const [editClientName, setEditClientName] = useState('')

  const {mutateAsync: createAlarmCenter, isPending: isCreating} = useCreateAlarmCenter()
  const {mutateAsync: updateAlarmCenter, isPending: isUpdating} = useUpdateAlarmCenter()

  const companyItems = companies.map((company) => ({id: `${company.id}`, label: company.name}))

  useEffect(() => {
    if (!isEdit || !alarmCenter) return

    async function loadClientName() {
      try {
        const client = (await api.client.getById(alarmCenter!.clientId)).data.data
        setEditClientName(client.name)
      } catch {
        setEditClientName(`#${alarmCenter!.clientId}`)
      }
    }

    void loadClientName()
  }, [alarmCenter, isEdit])

  useEffect(() => {
    if (isEdit || !form.companySelected.id) return
    void getClients(+form.companySelected.id)
  }, [form.companySelected.id, isEdit])

  const canSubmit = useMemo(() => {
    if (!isValidHardwareId(form.hardwareId) || !isValidAccountNumber(form.accountNumber) || !form.lineModel) return false
    if (isEdit) return canUpdate
    return canCreate && !!form.companySelected.id && !!form.clientSelected.id
  }, [form, isEdit, canCreate, canUpdate])

  const handleCompanyChange = (companySelected: DropdownItem) => {
    setForm((prev) => ({
      ...prev,
      companySelected,
      clientSelected: emptyDropdown,
    }))
  }

  const handleSubmit = async () => {
    if (!canSubmit || !form.lineModel) return

    try {
      if (isEdit && alarmCenter) {
        await updateAlarmCenter({
          id: alarmCenter.id,
          clientId: alarmCenter.clientId,
          hardwareId: form.hardwareId,
          accountNumber: form.accountNumber,
          lineModel: form.lineModel,
          isActive: form.isActive,
        })
      } else {
        await createAlarmCenter({
          hardwareId: form.hardwareId,
          accountNumber: form.accountNumber,
          lineModel: form.lineModel,
          companyId: +form.companySelected.id,
          clientId: +form.clientSelected.id,
          isActive: form.isActive,
        })
      }

      onClose()
    } catch {}
  }

  return (
    <FormModal
      isOpen
      onClose={onClose}
      title={isEdit ? 'Editar central de alarme' : 'Cadastrar central de alarme'}
      description={isEdit ? 'Atualize os dados da central vinculada ao cliente.' : 'Informe os dados da central e vincule ao cliente.'}
      width='560px'
      bodyGap='16px'
      onCancel={onClose}
      onSave={() => void handleSubmit()}
      saveDisabled={!canSubmit}
      isLoading={isCreating || isUpdating}
      saveLabel={isEdit ? 'Salvar' : 'Cadastrar'}>
      {isEdit ? (
        <Input.Root
          controlled
          label='Cliente'
          labelFontSize='14px'
          borderColor='#D0D5DD'
          fontSize='12px'
          disabled
          value={editClientName || `#${alarmCenter?.clientId}`}
          onChange={() => {}}
        />
      ) : (
        <>
          <Dropdown
            labelFontSize='14px'
            borderColor='#D0D5DD'
            fontSize='12px'
            selected={form.companySelected}
            items={companyItems}
            label='Empresa'
            onChangeSelected={handleCompanyChange}
            required
          />
          <Dropdown
            labelFontSize='14px'
            borderColor='#D0D5DD'
            fontSize='12px'
            selected={form.clientSelected}
            items={form.companySelected.id ? clientItems : []}
            label='Cliente'
            onChangeSelected={(clientSelected) => setForm((prev) => ({...prev, clientSelected}))}
            required
          />
        </>
      )}

      <Input.Root
        controlled
        label='Hardware ID'
        labelFontSize='14px'
        borderColor='#D0D5DD'
        fontSize='12px'
        labelFontWeight='400'
        maxLength={6}
        required
        value={form.hardwareId}
        onChange={(e) => setForm((prev) => ({...prev, hardwareId: e.target.value.toUpperCase().replace(/[^0-9A-F]/g, '')}))}
      />

      <Input.Root
        controlled
        type='tel'
        label='Número da conta'
        labelFontSize='14px'
        borderColor='#D0D5DD'
        fontSize='12px'
        labelFontWeight='400'
        maxLength={4}
        required
        value={form.accountNumber}
        onKeyDown={(event) => {
          const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End']
          if (allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey) return
          if (!/^\d$/.test(event.key)) event.preventDefault()
        }}
        onChange={(e) => setForm((prev) => ({...prev, accountNumber: e.target.value.replace(/\D/g, '')}))}
      />

      <FormControl fullWidth size='small'>
        <InputLabel id='alarm-center-line-model-label'>Modelo da linha</InputLabel>
        <Select
          labelId='alarm-center-line-model-label'
          label='Modelo da linha'
          value={form.lineModel}
          onChange={(e) => setForm((prev) => ({...prev, lineModel: e.target.value as AlarmCenterLineModel}))}>
          {LINE_MODEL_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {isEdit ? (
        <FormControlLabel
          control={<Switch checked={form.isActive} onChange={(e) => setForm((prev) => ({...prev, isActive: e.target.checked}))} />}
          label='Central ativa'
        />
      ) : null}
    </FormModal>
  )
}

function AlarmCentersModal({isOpen, alarmCenter, onClose}: Props) {
  const formKey = alarmCenter ? `edit-${alarmCenter.id}` : 'create'

  if (!isOpen) return null

  return <AlarmCentersForm key={formKey} alarmCenter={alarmCenter} onClose={onClose} />
}

export default AlarmCentersModal
